/**
 * WordPress dependencies.
 */
import { Fragment } from '@wordpress/element';
import { Disabled, BaseControl, TabPanel, PanelBody } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { applyFilters, addFilter } from '@wordpress/hooks';
import { useSelect } from '@wordpress/data';
import { getFragment } from '@wordpress/url';

/**
 * Exteranl dependencies.
 */
import { replace, isEmpty, has, last, merge } from 'lodash';

/**
 * Inner dependencies.
 */
import { getParsedVar } from '../../utils';
import { ColorControl } from '../../../extensions/controls/color-options';
import '../../../extensions/store/settings';

const ColorsSettings = ( props ) => {
	const { gStyle, changeSiteOptions, appState } = props;

	const siteOptions = useSelect( ( select ) => {
		return select( 'gutenify/settings' ).getSiteOptions();
	} );

	let coreStyles = {};
	let gutenifyStyles = {};

	if ( has( siteOptions, 'coreSettings.styles' ) ) {
		coreStyles = siteOptions.coreSettings.styles;
	}

	if ( has( siteOptions, 'settings.styles' ) ) {
		gutenifyStyles = siteOptions.settings.styles;
	}

	const setCoreStyles = ( data ) => {
		changeSiteOptions( merge(
			siteOptions,
			{
				coreSettings: {
					styles: merge( gStyle.styles, data ),
				},
			},
		) );
	};

	const setGutenifyStyles = ( data ) => {
		changeSiteOptions( merge(
			siteOptions,
			{
				settings: {
					styles: merge( gutenifyStyles, data ),
				},
			},
		) );
	};

	// const [ gutenifyStyles, setGutenifyStyles ] = useState( {} );

	const urlFragment = getFragment( window.location.href );
	const activePanel = replace( urlFragment, '#', '' );

	const getColorCode = ( colorString ) => {
		let colorCode = colorString;
		if ( colorString.indexOf( 'var(' ) > -1 ) {
			const parsedVar = getParsedVar( colorString );
			if ( ! isEmpty( parsedVar ) ) {
				if ( parsedVar.includes( 'preset' ) && parsedVar.includes( 'color' ) && has( gStyle, 'settings.color.palette.theme' ) ) {
					const presetColor = gStyle.settings.color.palette.theme.filter( ( themeColor ) => themeColor.slug === last( parsedVar ) );
					if ( ! isEmpty( presetColor ) ) {
						colorCode = presetColor[ 0 ].color;
					}
				} else if ( parsedVar.includes( 'custom' ) && parsedVar.includes( 'color' ) && has( gStyle, `settings.custom.color.${ last( parsedVar ) }` ) ) {
					colorCode = gStyle.settings.custom.color[ last( parsedVar ) ];
				}
			}

			if ( colorCode.indexOf( 'var(' ) > -1 ) {
				colorCode = getColorCode( colorCode );
			}
		}

		return colorCode;
	};

	// useEffect( ( ) => {
	// 	apiFetch( {
	// 		path: `/gutenify/v1/get_site_options`,
	// 		method: 'get',
	// 	} )
	// 		.then( ( res ) => {
	// 			if ( res.success && res.response ) {
	// 				setGutenifyStyles( res.response.styles );
	// 			}
	// 		} );
	// }, [ ] );

	const textColor = has( coreStyles, 'color.text' ) ? getColorCode( coreStyles.color.text ) : '';
	const backgroundColor = has( coreStyles, 'color.background' ) ? getColorCode( coreStyles.color.background ) : '';
	const h1Color = has( coreStyles, 'elements.h1.color.text' ) ? getColorCode( coreStyles.elements.h1.color.text ) : '';
	const buttonBackgroundColor = has( coreStyles, 'blocks.core/button.color.background' ) ? getColorCode( coreStyles.blocks[ 'core/button' ].color.background ) : '';
	const buttonTextColor = has( coreStyles, 'blocks.core/button.color.text' ) ? getColorCode( coreStyles.blocks[ 'core/button' ].color.text ) : '';

	const linkColor = has( coreStyles, 'elements.link.color.text' ) ? getColorCode( coreStyles.elements.link.color.text ) : '';
	// const linkHoverColor = has( gutenifyStyles, 'elements.linkHover.color.text' ) ? getColorCode( gutenifyStyles.elements.linkHover.color.text ) : '';

	// const buttonHoverTextColor = has( gutenifyStyles, 'blocks.core/button.hover.color.text' ) ? getColorCode( gutenifyStyles.blocks[ 'core/button' ].hover.color.text ) : '';
	// const buttonHoverBackgroundColor = has( gutenifyStyles, 'blocks.core/button.hover.color.background' ) ? getColorCode( gutenifyStyles.blocks[ 'core/button' ].hover.color.background ) : '';
	return <Disabled isDisabled={ appState.isSettingsDisabled }>
		<div className="gutenify-field-wrapper gutenify-field-grid">
			<div className="gutenify-field-label">
				<label htmlFor="text-color-control">{ __( 'Text Color' ) }</label>
			</div>
			<div className="gutenify-field-control">
				<ColorControl
					// label={ __( 'Text Color' ) }
					onChange={ ( color ) => {
						const data = merge( coreStyles, {
							color: {
								text: color,
							},
						} );
						setCoreStyles( { ...data } );
					} }
					value={ textColor }
					clearable={ false }
					id="ext-color-control"
				/>

			</div>
		</div>
		<div className="gutenify-field-wrapper gutenify-field-grid">
			<div className="gutenify-field-label">
				<label htmlFor="site-background-color-control">{ __( 'Site Background Color' ) }</label>
			</div>
			<div className="gutenify-field-control">
				<ColorControl
					// label={ __( 'Site Background Color' ) }
					onChange={ ( color ) => {
						const data = merge( coreStyles, {
							color: {
								background: color,
							},
						} );
						setCoreStyles( { ...data } );
					} }
					value={ backgroundColor }
					clearable={ false }
					id="site-background-color-control"
				/>
			</div>
		</div>
		<div className="gutenify-field-wrapper gutenify-field-grid">
			<div className="gutenify-field-label">
				<label htmlFor="site-background-color-control">{ __( 'Heading Color' ) }</label>
			</div>
			<div className="gutenify-field-control">
				<BaseControl help={ __( 'This will change color for H1 to H6 tags.' ) }>
					<ColorControl
						// label={ __( 'Heading Color' ) }
						onChange={ ( color ) => {
							const data = merge( coreStyles, {
								elements: {
									h1: {
										color: {
											text: color,
										},
									},
									h2: {
										color: {
											text: color,
										},
									},
									h3: {
										color: {
											text: color,
										},
									},
									h4: {
										color: {
											text: color,
										},
									},
									h5: {
										color: {
											text: color,
										},
									},
									h6: {
										color: {
											text: color,
										},
									},
								},
							} );
							setCoreStyles( { ...data } );
						} }
						value={ h1Color }
						clearable={ false }
					/>
				</BaseControl>
			</div>
		</div>

		<div className="gutenify-field-wrapper gutenify-field-grid">
			<div className="gutenify-field-label">
				<label htmlFor="site-background-color-control">{ __( 'Button Color' ) }</label>
			</div>
			<div className="gutenify-field-control">
				<TabPanel
					className="gutenify-editor-tab-panel gutenify-editor-tab-ovel"
					activeClass="active-tab"
					tabs={ [
						{
							name: 'button-color-normal',
							title: __( 'Normal' ),
							className: 'gutenify-color-normal-tab',
						},
						{
							name: 'button-color-hover',
							title: __( 'Hover' ),
							className: 'gutenify-color-hover-tab',
						},
					] }
				>
					{ ( tabInner ) => {
						return <>
							{ 'button-color-normal' === tabInner.name && <>
								<ColorControl
									label={ __( 'Button background Color' ) }
									onChange={ ( color ) => {
										const data = merge( coreStyles, {
											blocks: {
												'core/button': {
													color: {
														background: color,
													},
												},
											},
										} );
										setCoreStyles( { ...data } );
									} }
									value={ buttonBackgroundColor }
									clearable={ false }
								/>
								<ColorControl
									label={ __( 'Button Text Color' ) }
									onChange={ ( color ) => {
										const data = merge( coreStyles, {
											blocks: {
												'core/button': {
													color: {
														text: color,
													},
												},
											},
										} );
										setCoreStyles( { ...data } );
									} }
									value={ buttonTextColor }
									clearable={ false }
								/>

							</>
							}
							{ 'button-color-hover' === tabInner.name && <>
								{ applyFilters( 'gutenify--settings--design--colors-button-hover', [], { ...props, setCoreStyles, setGutenifyStyles, getColorCode } ) }
							</> }
						</>;
					} }
				</TabPanel>

			</div>
		</div>

		<div className="gutenify-field-wrapper gutenify-field-grid">
			<div className="gutenify-field-label">
				<label htmlFor="site-background-color-control">{ __( 'Link Color' ) }</label>
			</div>
			<div className="gutenify-field-control">
				<TabPanel
					className="gutenify-editor-tab-panel gutenify-editor-tab-ovel"
					activeClass="active-tab"
					tabs={ [
						{
							name: 'link-color-normal',
							title: __( 'Normal' ),
							className: 'gutenify-color-normal-tab',
						},
						{
							name: 'link-color-hover',
							title: __( 'Hover' ),
							className: 'gutenify-color-hover-tab',
						},
					] }
				>
					{ ( tabInner ) => {
						return <>
							{ 'link-color-normal' === tabInner.name && <>
								<ColorControl
									label={ __( 'Link Color' ) }
									onChange={ ( color ) => {
										const data = merge( coreStyles, {
											elements: {
												link: {
													color: {
														text: color,
													},
												},
											},
										} );
										setCoreStyles( { ...data } );
									} }
									value={ linkColor }
									clearable={ false }
								/>
							</> }
							{ 'link-color-hover' === tabInner.name && <>
								{ applyFilters( 'gutenify--settings--design--colors-link-hover', [], { ...props, setCoreStyles, setGutenifyStyles, getColorCode } ) }
							</> }
						</>;
					} }
				</TabPanel>

			</div>
		</div>
	</Disabled>;
};

addFilter( 'gutenify-admin-settings-design-panel', 'gutenify-admin-settings-design-panel-colors', ( content, props ) => (
	[ ...content, <Fragment key="gutenify-admin-settings-design-panel-colors">
		<PanelBody title={ __( 'Colors' ) } initialOpen={ 'general' === props.activePanel }>
			{ /* <div>{ __( 'Post Type' ) }<p className="description">{ __( 'Choose post types to activate Gutenburg.' ) }</p></div> */ }
			<ColorsSettings { ...props } />
		</PanelBody>
	</Fragment> ]
) );

const gutenifyComingSoonMessageCB = ( content ) => {
	if ( '1' === window._gutenify.pro_license_status ) {
		return content;
	}
	return [
		...content,
		<Fragment key="gutenify-admin-settings-panel-license">
			{ __( 'Coming soon...' ) }
		</Fragment>,
	];
};

addFilter( 'gutenify--settings--design--colors-link-hover', 'gutenify--settings--design--colors-link-hover-coming-soon-message', gutenifyComingSoonMessageCB, 11 );
addFilter( 'gutenify--settings--design--colors-button-hover', 'gutenify--settings--design--colors-button-hover-coming-soon-message', gutenifyComingSoonMessageCB, 11 );

